import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { TickIcon } from '@okcredit/web-lib'
import { THEME } from 'components/theme'
import { ReactComponent as SmsSvg } from 'assets/icons/sms-outline.svg'
import { ReactComponent as WhatsAppIcon } from 'assets/icons/subscription/whatsapp.svg'
import { TransactionAlertSettingsStyles } from './TransactionAlertSettings.styles'
import { AnchoredSheet } from './AnchoredSheet'

// Row glyphs take the disc's ink colour in both themes (KMP BenefitIconStyle.DEFAULT tint)
const ICON_FILL = { fill: 'currentColor' }

export type TxnAlertChannel = 'sms' | 'whatsapp'

interface TxnAlertChannelSheetProps {
  // Drawer / card to sit in; null centres it on the page
  anchor: HTMLElement | null
  selected: TxnAlertChannel | null
  onSelect: (channel: TxnAlertChannel) => void
  onProceed: () => void
  onDismiss: () => void
}

// Opened when an SMS-Premium merchant switches the alert on while a WhatsApp plan is on offer:
// "Transaction alert from OkCredit" — On SMS (stay, alert on) or On WhatsApp (go buy it).
// A copy of My Plan's variant picker without prices (KMP TxnAlertChannelSheet).
export const TxnAlertChannelSheet: FC<TxnAlertChannelSheetProps> = ({
  anchor,
  selected,
  onSelect,
  onProceed,
  onDismiss
}) => {
  const classes = TransactionAlertSettingsStyles()
  const { t } = useTranslation()
  const rows: Array<{
    id: TxnAlertChannel
    label: string
    Icon: FC<React.SVGProps<SVGSVGElement>>
  }> = [
    { id: 'sms', label: t('dashboard:AlertChannelOnSms'), Icon: SmsSvg },
    { id: 'whatsapp', label: t('dashboard:AlertChannelOnWhatsapp'), Icon: WhatsAppIcon }
  ]
  return (
    <AnchoredSheet
      anchor={anchor}
      ariaLabel={t('dashboard:AlertChannelSheetTitle')}
      onDismiss={onDismiss}
    >
      <div className={classes.channelSheetBody}>
        <div className={classes.channelHeader}>
          <span className={classes.channelTitle}>{t('dashboard:AlertChannelSheetTitle')}</span>
          <button
            type="button"
            className={classes.channelClose}
            onClick={onDismiss}
            aria-label={t('dashboard:Cancel')}
          >
            ×
          </button>
        </div>
        {rows.map(({ id, label, Icon }) => {
          const picked = selected === id
          return (
            <div
              key={id}
              role="radio"
              aria-checked={picked}
              tabIndex={0}
              className={classNames(classes.channelRow, { [classes.channelRowSelected]: picked })}
              onClick={() => onSelect(id)}
              onKeyDown={(e) => e.key === 'Enter' && onSelect(id)}
            >
              <span className={classes.channelDisc}>
                {/* Inline fill beats the SVG's own baked fill attribute (sms-outline is #9e9e9e) */}
                <Icon style={ICON_FILL} />
              </span>
              <span className={classes.channelLabel}>{label}</span>
              <span className={classes.channelMark}>
                {picked ? (
                  <span className={classes.channelCheck}>
                    <TickIcon size={14} fill={THEME.onBrand} />
                  </span>
                ) : (
                  <span className={classes.channelRing} />
                )}
              </span>
            </div>
          )
        })}
        <button
          type="button"
          className={classes.channelProceed}
          disabled={!selected}
          onClick={onProceed}
        >
          {t('dashboard:Proceed')}
        </button>
      </div>
    </AnchoredSheet>
  )
}

export default TxnAlertChannelSheet
